import { Controller } from '@hotwired/stimulus';
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';
import './cropper_controller.scss';
import { putCacheImage } from '../utils/axios_request';

// A photo is far smaller as JPEG, but a PNG the user picked may be carrying
// transparency, and flattening it here would destroy it before the server ever
// sees it. PhotoUploader quantises such a file instead if it comes in too big.
const PNG_MIME = 'image/png';
const DEFAULT_MIME = 'image/jpeg';
const CROPPED_QUALITY = 0.85;

export default class extends Controller {
  static targets = [
    'removeImageInput', 'removeButton', 'imageCacheInput', 'fileInput', 'modal',
    'image', 'preview', 'validationStatus', 'croppedImagePreview'
  ];

  static values = {
    uploadUrl: String
  };

  static classes = ['displayNone'];

  initialize() {
    $(this.modalTarget).on('shown.bs.modal', () => {
      const self = this;
      this.previewReady = false;
      this.cropper = new Cropper(this.imageTarget, {
        aspectRatio: 1,
        viewMode: 3,
        ready() {
          self.onCropperReady(this);
        },
        crop: () => this.refreshPreview()
      });
    });
    $(this.modalTarget).on('hidden.bs.modal', () => {
      this.clearCropperAndPreviews();
    });
  }

  // ACTIONS

  selectImage() {
    this.fileInputTarget.click();
  }

  imageSelected(e) {
    this.setValidationStatus('');
    const file = e.target.files[0];
    const { hasErrors, errorId } = window.FileUpload.checkSizeAndExtension(e.target);
    if (hasErrors) {
      this.setValidationStatus(this.getErrorMessage(errorId));
      this.clearFileInput();
    } else {
      this.originalFilename = file.name;
      this.croppedMime = file.type === PNG_MIME ? PNG_MIME : DEFAULT_MIME;
      this.showPreview(file);
    }
  }

  removeImage() {
    this.removeImageInputTarget.value = true;
    this.croppedImagePreviewTarget.src = this.croppedImagePreviewTarget.dataset.fallbackUrl;
    this.removeButtonTarget.classList.add(this.displayNoneClass);
  }

  finalizeCropping() {
    // toBlob() with no type encodes as PNG, so a photo straight from a phone used
    // to come back many times heavier than the file the user picked. The check in
    // imageSelected() never caught it, that one ran on the original file.
    const canvas = this.cropper.getCroppedCanvas(this.croppedCanvasOptions());
    canvas.toBlob((blob) => {
      putCacheImage(this.uploadUrlValue, blob, this.originalFilename).then((response) => {
        this.removeButtonTarget.classList.remove(this.displayNoneClass);
        this.removeImageInputTarget.value = false;
        const cache = response.data[0].image_cache;
        const croppedPreviewUrl = response.data[0].thumbnail_url;
        this.imageCacheInputTarget.value = cache;
        this.croppedImagePreviewTarget.src = croppedPreviewUrl;
        // clear file input so that uncropped version is not submitted
        // cropped version in submitted via cache input field
        this.clearFileInput();
        $(this.modalTarget).modal('hide');
      }).catch((error) => {
        this.setValidationStatus(this.uploadErrorMessage(error));
        this.clearFileInput();
      });
    }, this.croppedMime, CROPPED_QUALITY);
  }

  // PRIVATE

  showPreview(file) {
    const reader = new FileReader();
    reader.onload = () => { this.openCropperModal(reader.result) };
    reader.readAsDataURL(file);
  }

  openCropperModal(srcUrl) {
    this.imageTarget.src = srcUrl;
    $(this.modalTarget).modal({
      backdrop: 'static',
      keyboard: false
    });
  }

  onCropperReady(img) {
    const clone = img.cloneNode();
    clone.className = '';
    clone.style.cssText = (
      'display: block;'
      + 'width: 100%;'
      + 'min-width: 0;'
      + 'min-height: 0;'
      + 'max-width: none;'
      + 'max-height: none;'
    );
    Array.from(this.previewTargets).forEach((p) => {
      p.appendChild(clone.cloneNode());
    });
    this.previewReady = true;
    this.refreshPreview();
  }

  refreshPreview() {
    if (!this.previewReady) {
      return;
    }
    const data = this.cropper.getData();
    const imageData = this.cropper.getImageData();
    Array.from(this.previewTargets).forEach((p) => {
      // calculations taken from cropper js examples
      const previewImage = p.children[0];
      const previewWidth = p.offsetWidth;
      const previewHeight = p.offsetWidth; // square ratio
      const imageScaledRatio = data.width / previewWidth;

      p.style.height = previewHeight + 'px';
      previewImage.style.width = imageData.naturalWidth / imageScaledRatio + 'px';
      previewImage.style.height = imageData.naturalHeight / imageScaledRatio + 'px';
      previewImage.style.marginLeft = -data.x / imageScaledRatio + 'px';
      previewImage.style.marginTop = -data.y / imageScaledRatio + 'px';
    });
  }

  clearCropperAndPreviews() {
    this.cropper.destroy();
    this.cropper = null;
    Array.from(this.previewTargets).forEach((p) => {
      p.innerHTML = '';
    });
  }

  getFileName() {
    return this.fileInputTarget.files[0].name;
  }

  getErrorMessage(errorId) {
    return document.getElementById(errorId).textContent
      .replace(/%\(file\)/, this.getFileName());
  }

  // cropperjs paints fillColor across the whole canvas before drawing the image,
  // both in getCroppedCanvas and again in getSourceCanvas, so it flattens the
  // source's own alpha and not only the area a crop box reaches past. JPEG has no
  // alpha to lose and would render those pixels black without it. PNG must go
  // without, otherwise nothing transparent survives and picking PNG buys nothing.
  croppedCanvasOptions() {
    return this.croppedMime === PNG_MIME ? {} : { fillColor: '#fff' };
  }

  // The endpoint answers with an array, the same shape the success path unwraps
  // with data[0]. Reading .error straight off it yields undefined, which is what
  // the box used to display. A request that never got a response has no body at
  // all, so fall back rather than throwing inside the catch and showing nothing.
  uploadErrorMessage(error) {
    const body = error.response && error.response.data;
    const payload = Array.isArray(body) ? body[0] : body;
    return (payload && payload.error) || this.genericUploadError();
  }

  genericUploadError() {
    return this.validationStatusTarget.dataset.genericError || 'Upload failed';
  }

  setValidationStatus(value) {
    this.validationStatusTarget.classList.remove(this.displayNoneClass);
    if (value === '') {
      this.validationStatusTarget.classList.add(this.displayNoneClass);
    }
    this.validationStatusTarget.textContent = value;
  }

  clearFileInput() {
    this.fileInputTarget.value = '';
  }
}
